import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  CaretLeft,
  FloppyDisk,
  Trash,
  Plus,
  Phone,
  EnvelopeSimple,
  MapPin,
  ChatCircle
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const STATUS_LABELS = {
  nouveau: "Nouveau", contacte: "Contacté", en_discussion: "En discussion",
  offre_envoyee: "Offre envoyée", reserve: "Réservé", converti: "Converti", perdu: "Perdu"
};
const INTERACTION_TYPES = {
  appel: "Appel téléphonique", email: "Email", visite: "Visite en magasin", sms: "SMS", autre: "Autre"
};
const TYPE_LABELS = { particulier: "Particulier", professionnel: "Professionnel", prospect: "Prospect" };

/* ─── Sub-components ─── */

const ClientInfoForm = ({ clientForm, setClientForm, canEdit, saving, onSave }) => (
  <div className="card p-6">
    <h2 className="text-heading-3 mb-4">Informations</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div><Label className="form-label">Prénom</Label><Input value={clientForm.prenom || ""} onChange={(e) => setClientForm({ ...clientForm, prenom: e.target.value })} disabled={!canEdit} /></div>
      <div><Label className="form-label">Nom</Label><Input value={clientForm.nom || ""} onChange={(e) => setClientForm({ ...clientForm, nom: e.target.value })} disabled={!canEdit} /></div>
      <div><Label className="form-label">Téléphone</Label><Input value={clientForm.telephone || ""} onChange={(e) => setClientForm({ ...clientForm, telephone: e.target.value })} disabled={!canEdit} /></div>
      <div><Label className="form-label">Téléphone 2</Label><Input value={clientForm.telephone_2 || ""} onChange={(e) => setClientForm({ ...clientForm, telephone_2: e.target.value })} disabled={!canEdit} /></div>
      <div><Label className="form-label">Email</Label><Input type="email" value={clientForm.email || ""} onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })} disabled={!canEdit} /></div>
      <div><Label className="form-label">Type client</Label>
        <Select value={clientForm.type_client || "prospect"} onValueChange={(val) => setClientForm({ ...clientForm, type_client: val })} disabled={!canEdit}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(TYPE_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2"><Label className="form-label">Adresse</Label><Input value={clientForm.adresse || ""} onChange={(e) => setClientForm({ ...clientForm, adresse: e.target.value })} disabled={!canEdit} /></div>
      <div><Label className="form-label">Code postal</Label><Input value={clientForm.code_postal || ""} onChange={(e) => setClientForm({ ...clientForm, code_postal: e.target.value })} disabled={!canEdit} /></div>
      <div><Label className="form-label">Ville</Label><Input value={clientForm.ville || ""} onChange={(e) => setClientForm({ ...clientForm, ville: e.target.value })} disabled={!canEdit} /></div>
      <div><Label className="form-label">N° Registre National</Label><Input value={clientForm.numero_registre_national || ""} onChange={(e) => setClientForm({ ...clientForm, numero_registre_national: e.target.value })} disabled={!canEdit} placeholder="YY.MM.DD-XXX.XX" data-testid="input-rrn" /></div>
      <div><Label className="form-label">N° TVA</Label><Input value={clientForm.numero_tva || ""} onChange={(e) => setClientForm({ ...clientForm, numero_tva: e.target.value })} disabled={!canEdit} placeholder="BE 0XXX.XXX.XXX" data-testid="input-tva" /></div>
      <div><Label className="form-label">Statut commercial</Label>
        <Select value={clientForm.statut_commercial || "nouveau"} onValueChange={(val) => setClientForm({ ...clientForm, statut_commercial: val })} disabled={!canEdit}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(STATUS_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}</SelectContent>
        </Select>
      </div>
      <div><Label className="form-label">Source du lead</Label><Input value={clientForm.source_lead || ""} onChange={(e) => setClientForm({ ...clientForm, source_lead: e.target.value })} disabled={!canEdit} placeholder="Site web, Autoscout..." /></div>
    </div>
    <div className="mt-6 pt-6 border-t border-slate-200">
      <Label className="form-label">Notes commerciales</Label>
      <Textarea value={clientForm.notes_commerciales || ""} onChange={(e) => setClientForm({ ...clientForm, notes_commerciales: e.target.value })} disabled={!canEdit} rows={4} placeholder="Notes internes..." />
    </div>
    <div className="mt-6 pt-6 border-t border-slate-200 grid grid-cols-2 gap-4">
      <div><Label className="form-label">Lien annonce</Label><Input value={clientForm.lien_annonce || ""} onChange={(e) => setClientForm({ ...clientForm, lien_annonce: e.target.value })} disabled={!canEdit} placeholder="https://..." /></div>
      <div><Label className="form-label">Lien Car-Pass</Label><Input value={clientForm.lien_carpass || ""} onChange={(e) => setClientForm({ ...clientForm, lien_carpass: e.target.value })} disabled={!canEdit} placeholder="https://..." /></div>
    </div>
    {canEdit && (
      <div className="flex justify-end mt-6">
        <Button onClick={onSave} disabled={saving} className="btn-primary" data-testid="save-client-btn">
          <FloppyDisk size={18} className="mr-2" />{saving ? "Enregistrement..." : "Enregistrer"}
        </Button>
      </div>
    )}
  </div>
);

const InteractionHistory = ({ interactions, canEdit, onAddInteraction }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newInteraction, setNewInteraction] = useState({ type_interaction: "appel", sujet: "", contenu: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    onAddInteraction(newInteraction);
    setDialogOpen(false);
    setNewInteraction({ type_interaction: "appel", sujet: "", contenu: "" });
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-heading-3">Historique</h2>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" data-testid="add-interaction-btn"><Plus size={14} className="mr-1" /> Ajouter</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouvelle interaction</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div><Label className="form-label">Type</Label>
                  <Select value={newInteraction.type_interaction} onValueChange={(val) => setNewInteraction({ ...newInteraction, type_interaction: val })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(INTERACTION_TYPES).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div><Label className="form-label">Sujet *</Label><Input value={newInteraction.sujet} onChange={(e) => setNewInteraction({ ...newInteraction, sujet: e.target.value })} required data-testid="interaction-sujet" /></div>
                <div><Label className="form-label">Contenu</Label><Textarea value={newInteraction.contenu} onChange={(e) => setNewInteraction({ ...newInteraction, contenu: e.target.value })} rows={4} data-testid="interaction-contenu" /></div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                  <Button type="submit" className="btn-primary" data-testid="submit-interaction-btn">Ajouter</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>
      {interactions.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <ChatCircle size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">Aucune interaction enregistrée</p>
        </div>
      ) : (
        <div className="timeline">
          {interactions.map((interaction) => (
            <div key={interaction.interaction_id} className="timeline-item">
              <p className="timeline-time">{new Date(interaction.created_at).toLocaleDateString("fr-BE")} - {INTERACTION_TYPES[interaction.type_interaction] || interaction.type_interaction}</p>
              <div className="timeline-content">
                <p className="font-medium text-slate-900 text-sm">{interaction.sujet}</p>
                {interaction.contenu && <p className="text-slate-600 text-xs mt-1">{interaction.contenu}</p>}
                {interaction.user_name && <p className="text-slate-400 text-xs mt-1">par {interaction.user_name}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const QuickContact = ({ client }) => (
  <div className="card p-6">
    <h2 className="text-heading-3 mb-4">Contact rapide</h2>
    <div className="space-y-2">
      {client?.telephone && (
        <a href={`tel:${client.telephone}`} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
          <Phone size={18} className="text-blue-600" /><span className="text-slate-900 text-sm">{client.telephone}</span>
        </a>
      )}
      {client?.email && (
        <a href={`mailto:${client.email}`} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
          <EnvelopeSimple size={18} className="text-blue-600" /><span className="text-slate-900 text-sm">{client.email}</span>
        </a>
      )}
      {client?.adresse && (
        <div className="flex items-start gap-3 p-3 rounded-lg border border-slate-200">
          <MapPin size={18} className="text-blue-600 flex-shrink-0 mt-0.5" /><span className="text-slate-900 text-sm">{client.adresse} {client.code_postal} {client.ville}</span>
        </div>
      )}
    </div>
  </div>
);

/* ─── Main Component ─── */

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [client, setClient] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clientForm, setClientForm] = useState({});

  const canEdit = user?.role === "admin" || user?.role === "commercial";
  const canDelete = user?.role === "admin";

  const fetchData = useCallback(async () => {
    try {
      const [clientRes, interactionsRes] = await Promise.all([
        axios.get(`${API}/clients/${id}`),
        axios.get(`${API}/clients/${id}/interactions`)
      ]);
      setClient(clientRes.data);
      setClientForm(clientRes.data);
      setInteractions(interactionsRes.data);
    } catch (error) {
      console.error("Error fetching client:", error);
      toast.error("Erreur lors du chargement du client");
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveClient = async () => {
    setSaving(true);
    try {
      const { client_id, created_at, updated_at, created_by, ...updateData } = clientForm;
      await axios.put(`${API}/clients/${id}`, updateData);
      toast.success("Client mis à jour");
      setClient(clientForm);
    } catch (error) { toast.error("Erreur lors de la sauvegarde"); }
    finally { setSaving(false); }
  };

  const handleAddInteraction = async (data) => {
    try {
      await axios.post(`${API}/clients/${id}/interactions`, null, {
        params: { type_interaction: data.type_interaction, sujet: data.sujet, contenu: data.contenu }
      });
      toast.success("Interaction ajoutée");
      fetchData();
    } catch (error) { toast.error("Erreur lors de l'ajout"); }
  };

  const handleDeleteClient = async () => {
    try {
      await axios.delete(`${API}/clients/${id}`);
      toast.success("Client supprimé");
      navigate("/clients");
    } catch (error) { toast.error("Erreur lors de la suppression"); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner h-10 w-10"></div></div>;
  }

  return (
    <div data-testid="client-detail-page" className="animate-fade-in">
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Link to="/clients" data-testid="back-to-clients" className="p-2 hover:bg-slate-100 rounded-lg transition-colors"><CaretLeft size={20} /></Link>
          <div>
            <h1 className="page-title">{client?.prenom} {client?.nom}</h1>
            <p className="page-subtitle">{TYPE_LABELS[client?.type_client] || "Client"} depuis le {new Date(client?.created_at).toLocaleDateString("fr-BE")}</p>
          </div>
          <span className={`badge badge-${client?.statut_commercial}`}>{STATUS_LABELS[client?.statut_commercial] || client?.statut_commercial}</span>
        </div>
        <div className="page-actions">
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="text-red-600 hover:text-red-700" data-testid="delete-client-btn"><Trash size={18} className="mr-2" /> Supprimer</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Supprimer ce client ?</AlertDialogTitle><AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={handleDeleteClient} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">Supprimer</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ClientInfoForm clientForm={clientForm} setClientForm={setClientForm} canEdit={canEdit} saving={saving} onSave={handleSaveClient} />
        </div>
        <div className="space-y-6">
          <InteractionHistory interactions={interactions} canEdit={canEdit} onAddInteraction={handleAddInteraction} />
          <QuickContact client={client} />
        </div>
      </div>
    </div>
  );
};

export default ClientDetail;
